import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import tw, { css } from 'twin.macro'

import { useWindowSize } from 'common/hooks/useWindowSize'
import { useEventListener } from 'common/hooks/useEventListener'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useDebounce } from 'common/hooks/useDebounce'
import { useDebouncedTextMutation } from 'common/hooks/useDebouncedTextMutation'
import { useListenExistingItemDraftsQuery } from 'modules/Uploads/uploadApi'
import { useListenOneTimeProductItemTypeQuery } from 'modules/ProductInfo/productInfoApi'

import { CHECKOUT_FIELDS_MAXLENGTH } from 'modules/PageBuilder/constants/checkoutConstants'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { buttonBase, buttonSizes, buttonVariants } from 'common/components/Button/Button'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'
import { BenefitsInputList } from '../BenefitsList/BenefitsList'
import PreviewInput from '../Inputs/PreviewInput'
import ImageUpload from '../Inputs/ImageUpload'
import { DisplayModeSwitch } from '../DisplayModeSwitch/DisplayModeSwitch'
import PricingList from 'modules/ProductInfo/components/PricingList/PricingList'

import logo from 'assets/images/TrybeLogoWhite.svg'
import appStore from 'assets/images/download-app-store.svg'
import googlePlay from 'assets/images/download-google-play.svg'
import {
  useUpdateCheckoutHeroMainTextMutation,
  useUpdateCheckoutHeroMutation,
} from 'modules/PageBuilder/pageBuilderApi'
import { SignupPreview } from './SignupPreview'
import Product from './Product'
import { PAGE_MAX_WIDTH } from 'modules/PageBuilder/pages/CheckoutPageBuilder'

export const inputRefsSortMethod = ['heroTitle', 'benefit', 'addBenefitBtn']
const PAGE_HORIZONTAL_SPACING = 68
const HEADER_HEIGHT = 64

export default function CheckoutPreview({ coachOrgId, page, productPlans, coupons, handlePublishPage, isPublishing }) {
  const { productId } = useParams()
  const [heroMainText, setHeroMainText] = useState(page?.heroSection?.mainText || '')
  const [displayMode, setDisplayMode] = useState('desktop')
  const isMob = displayMode === 'mobile'

  const [updateHero] = useUpdateCheckoutHeroMutation()

  const { data: isImgUploading } = useListenExistingItemDraftsQuery({ coachOrgId, id: 'checkout-hero-image' })
  const { data: productItemType } = useListenOneTimeProductItemTypeQuery({ coachOrgId, productId })

  const [updateCheckoutHeroMainText] = useUpdateCheckoutHeroMainTextMutation()
  const debouncedHeroMainText = useDebounce(heroMainText, 500)
  useDebouncedTextMutation({
    stateText: heroMainText,
    dbText: page?.heroSection?.mainText || '',
    mutation: updateCheckoutHeroMainText,
    debouncedStateText: debouncedHeroMainText,
    mutationArgs: {
      coachOrgId,
      productId,
      text: debouncedHeroMainText,
    },
  })

  const [windowWidth, windowHeight] = useWindowSize()
  const screenAspectRatio = Math.round((windowWidth / windowHeight) * 100) / 100
  const heroImgHeight =
    Math.round(((PAGE_MAX_WIDTH - PAGE_HORIZONTAL_SPACING) / screenAspectRatio) * 100) / 100 - HEADER_HEIGHT

  const clearHeroImage = async () => {
    await updateHero({ coachOrgId, productId, image: null })
  }

  const { addInputRef, moveFocusOnKeyPress } = useFormRefsControl()
  const heroTitleRef = useRef()
  useEffect(() => {
    addInputRef({ ref: heroTitleRef, name: 'heroTitle', sortMethod: inputRefsSortMethod })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEventListener('keydown', moveFocusOnKeyPress)

  const disablePublish = productId && !productItemType
  return (
    <>
      <div className='flex justify-between items-end mb-5 w-full'>
        <div className='w-1/3 h-[60px]' />
        <div className='flex justify-center w-1/3'>
          <DisplayModeSwitch displayMode={displayMode} setDisplayMode={setDisplayMode} />
        </div>
        <div className='w-1/3 flex justify-end'>
          <Dialog>
            <Tooltip
              content='Please select a content format in the "Content" tab before publishing'
              open={disablePublish ? undefined : false}
            >
              <DialogTrigger
                css={[buttonBase, buttonSizes.lg, buttonVariants.primary]}
                aria-label='Publish'
                disabled={disablePublish}
              >
                Publish page
              </DialogTrigger>
            </Tooltip>
            <DialogContent>
              <CreateDialogBanner
                text={`This will publish the changes of Checkout Page`}
                handleCreate={handlePublishPage}
                createBtnText='Publish'
                loading={isPublishing}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div css={[tw`flex flex-col w-full`, isMob && tw`max-w-sm border-offWhite border`]}>
        <div css={[tw`flex items-center px-2 py-4 bg-black border-x border-offWhite`, isMob && tw`border-x-0`]}>
          <img src={logo} alt='logo' className='w-8 h-8' />
          <span css={[tw`text-3xl text-white uppercase ml-2 leading-none tracking-widest`, isMob && tw`text-xl`]}>
            TRYBE
          </span>
        </div>
        <div
          className='flex flex-1'
          css={[isMob && tw`flex-col`, !isMob && tw`border-offWhite border-b border-l`]}
          style={{
            maxHeight: isMob ? undefined : heroImgHeight,
          }}
        >
          <div
            css={[
              heroBgClasses,
              page?.heroImage &&
                css`
                  background-image: url(${page.heroImage});
                `,
              isMob && tw`hidden`,
            ]}
            className='group'
          >
            <div
              css={[tw`invisible absolute top-5 right-5 z-20`, isImgUploading && tw`visible`]}
              className='group-hover:visible'
            >
              <ImageUpload
                coachOrgId={coachOrgId}
                id={productId || 'checkout-hero-image'}
                uploadType={productId ? 'one-time-checkout-hero' : 'checkout-hero-image'}
                clearImage={clearHeroImage}
              >
                Upload 16:9 image
              </ImageUpload>
            </div>
            <div className='flex flex-col items-center z-10 w-full'>
              <PreviewInput
                inputRef={heroTitleRef}
                name='heroTitle'
                inputCss={[
                  tw`min-h-[60px] text-center text-white text-5xl font-bold mb-12 placeholder:text-gray-200`,
                  isMob && tw`text-2xl`,
                ]}
                style={{ textShadow: '2px 2px rgba(0,0,0,.3)' }}
                value={heroMainText}
                maxLength={CHECKOUT_FIELDS_MAXLENGTH.appTitle}
                onChange={(_, value) => {
                  const lines = value.split('\n')
                  let adjustedValue = value
                  if (lines.length > 2) {
                    adjustedValue = lines.slice(0, 2).join('\n')
                  }
                  setHeroMainText(adjustedValue)
                }}
                placeholder='Transform Your Fitness—Join Now'
              />
              <div className='flex items-center'>
                <img src={appStore} alt='app store download' />
                <img src={googlePlay} alt='google play download' className='ml-2.5' />
              </div>
            </div>
          </div>
          <div
            className='flex flex-col shrink-0 bg-white'
            css={[
              isMob ? tw`w-full py-4` : tw`w-[475px] py-6 overflow-y-scroll`,
              !isMob && tw`border-offWhite border-b border-r`,
            ]}
          >
            <div className='flex flex-col mb-10'>
              <h3
                className='text-tGreen font-bold mb-3 cursor-not-allowed'
                css={[isMob ? tw`mx-6 text-xl` : tw`mx-8 text-2xl`]}
              >
                1. {productId ? 'Your product' : 'Select your plan'}
              </h3>
              <div className='mb-5' css={[isMob ? tw`mx-6` : tw`mx-8`]}>
                {productId ? (
                  <Product coachOrgId={coachOrgId} productId={productId} coupons={coupons} />
                ) : (
                  <PricingList
                    coachOrgId={coachOrgId}
                    coupons={coupons}
                    plans={productPlans}
                    loading={false}
                    isMob={isMob}
                  />
                )}
              </div>
              <div className='flex flex-col'>
                <span className='text-lg font-bold mb-1 cursor-not-allowed' css={[isMob ? tw`px-6` : tw`px-8`]}>
                  What you get
                </span>
                <BenefitsInputList
                  coachOrgId={coachOrgId}
                  productId={productId}
                  benefits={page?.benefits || []}
                  isMob={isMob}
                />
              </div>
            </div>
            <div className='flex flex-col mb-10' css={[isMob ? tw`px-6` : tw`px-8`]}>
              <h3 className='text-black font-bold mb-3 cursor-not-allowed' css={[isMob ? tw`text-xl` : tw`text-2xl`]}>
                2. Create a Trybe account
              </h3>
              <SignupPreview />
            </div>
            <div className='flex flex-col' css={[isMob ? tw`px-6` : tw`px-8`]}>
              <h3 className='text-black font-bold mb-3 cursor-not-allowed' css={[isMob ? tw`text-xl` : tw`text-2xl`]}>
                3. Enter billing info
              </h3>
              <div className='flex items-center justify-center text-white w-full rounded-lg bg-black py-4 font-bold cursor-not-allowed'>
                Pay
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const heroBgClasses = tw`
  relative flex p-10 flex-1 
  flex-col items-center 
  justify-center overflow-hidden 
  bg-cover bg-center before:absolute 
  before:top-0 before:left-0 
  before:w-full before:h-full 
  before:bg-black before:opacity-20
`
